// ---------------------------------------------------------------------------
// PromptBlock — Individual prompt card in the tree
// ---------------------------------------------------------------------------
// CMS-01 additions (2026-06-01 walked decision):
//   - Unreachable-prompt warning chip when no answer routes to this prompt
//     and it isn't a root (`is_reachable` prop, computed in PromptTree).
//   - "Routes to" labels show "#sort_order · S{session}" instead of a raw
//     UUID prefix, looked up via the promptLabels map passed in.

import { Info, Pencil, Trash2, Volume2, AlertTriangle, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { TemplatePrompt, AlertLevel } from '@/types/template';

const ALERT_COLORS: Record<AlertLevel, string> = {
  0: 'bg-green-100 text-green-700 border-green-200',
  1: 'bg-green-50 text-green-600 border-green-100',
  2: 'bg-honey-100 text-honey-800 border-honey-200',
  3: 'bg-red-100 text-red-700 border-red-200',
  4: 'bg-red-200 text-red-800 border-red-300',
};

const DEPTH_BORDERS = [
  '',
  'border-l-purple-300',
  'border-l-teal-400',
  'border-l-amber-300',
  'border-l-pink-300',
];

interface PromptBlockProps {
  prompt: TemplatePrompt;
  indented?: boolean;
  /** Nesting depth for visual indentation (0 = root, 1+ = nested). */
  depth?: number;
  /**
   * CMS-01: false when this prompt has no answer routing to it AND it
   * isn't a root — surfaces a visual warning chip so authors notice
   * orphan prompts before publishing.
   */
  isReachable?: boolean;
  /**
   * CMS-01: prompt_id -> display label (e.g. "#3 · S1"). Used to render
   * readable "routes to" arrows on each answer chip.
   */
  promptLabels?: Map<string, string>;
  onEdit: (prompt: TemplatePrompt) => void;
  onDelete: (promptId: string) => void;
  onAudio: (prompt: TemplatePrompt) => void;
  onAddNested?: (prompt: TemplatePrompt, answerId: string) => void;
  readOnly?: boolean;
}

export function PromptBlock({
  prompt,
  indented,
  depth = 0,
  isReachable = true,
  promptLabels,
  onEdit,
  onDelete,
  onAudio,
  onAddNested,
  readOnly,
}: PromptBlockProps) {
  const nestLevel = depth || (indented ? 1 : 0);
  const borderColor = DEPTH_BORDERS[Math.min(nestLevel, DEPTH_BORDERS.length - 1)];

  return (
    <Card
      className={
        (nestLevel > 0 ? `border-l-2 ${borderColor}` : '') +
        (!isReachable ? ' border-honey-300 bg-honey-50/30' : '')
      }
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Prompt header */}
            <div className="flex items-center gap-2 mb-1">
              {prompt.is_informational ? (
                <Badge variant="secondary" className="gap-1">
                  <Info className="h-3 w-3" /> Info
                </Badge>
              ) : (
                <Badge variant="default">
                  Q-{prompt.question_id}
                </Badge>
              )}
              <span className="text-xs text-carbon-400">
                #{prompt.sort_order}
              </span>
              {!isReachable && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="destructive"
                      className="gap-1 bg-honey-200 text-honey-900 border-honey-300"
                    >
                      <AlertTriangle className="h-3 w-3" /> Unreachable
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    No answer routes to this prompt, it isn&apos;t a root, and
                    the previous prompt has explicit branching. Patients
                    will never see it.
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {/* Prompt text */}
            <p className="text-sm text-carbon-800 leading-relaxed">
              {prompt.is_informational
                ? prompt.prompt_text
                : prompt.prompt_text ?? `(Question #${prompt.question_id})`}
            </p>

            {/* Answers */}
            {prompt.answers.length > 0 && (
              <div className="mt-2 space-y-1">
                {prompt.answers
                  .sort((a, b) => a.sort_order - b.sort_order)
                  .map((answer) => (
                    <div
                      key={answer.prompt_answer_id}
                      className={`flex items-center gap-2 rounded-ds-004 border px-3 py-1 text-xs ${ALERT_COLORS[answer.alert_level]}`}
                    >
                      <span className="font-medium shrink-0">L{answer.alert_level}</span>
                      <span className="flex-1">{answer.answer_text}</span>
                      {answer.keypress && (
                        <span className="text-[10px] opacity-70">Key: {answer.keypress}</span>
                      )}
                      {answer.next_prompt_id && (
                        <span className="text-[10px] font-medium opacity-80">
                          &rarr;{' '}
                          {promptLabels?.get(answer.next_prompt_id) ??
                            `prompt ${answer.next_prompt_id.slice(0, 8)}…`}
                        </span>
                      )}
                      {!readOnly && onAddNested && !answer.next_prompt_id && nestLevel < 1 && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="shrink-0 rounded p-0.5 hover:bg-black/10 text-current opacity-60 hover:opacity-100"
                              onClick={() => onAddNested(prompt, answer.prompt_answer_id)}
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Add follow-up for &ldquo;{answer.answer_text}&rdquo;
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  ))}
              </div>
            )}

          </div>

          {/* Action buttons */}
          {!readOnly && (
            <div className="flex items-center gap-1 shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onAudio(prompt)}
                  >
                    <Volume2 className="h-4 w-4 text-purple-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Audio / Translations</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => onEdit(prompt)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit Prompt</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-500 hover:text-red-700"
                    onClick={() => onDelete(prompt.prompt_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete Prompt</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
